import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Mail, MapPin, Loader2 } from "lucide-react";

const FORMSPREE_ENDPOINT =
  process.env.REACT_APP_FORMSPREE_ENDPOINT || "https://formspree.io/f/mvzezoan";

const DEVICE_OPTIONS = [
  { id: "macbook", label: "MacBook" },
  { id: "imac", label: "iMac" },
  { id: "macmini", label: "Mac mini" },
  { id: "ipad", label: "iPad" },
  { id: "iphone", label: "iPhone" },
  { id: "applewatch", label: "Apple Watch" },
  { id: "other", label: "Annet" },
];

const ACCESSORY_OPTIONS = [
  { id: "charger", label: "Lader" },
  { id: "cable", label: "Kabel" },
  { id: "case", label: "Deksel" },
  { id: "kbmouse", label: "Tastatur/mus" },
  { id: "other", label: "Annet" },
];

const TERMS = [
  "Appledoctor kan utføre feilsøking og diagnostikk på enheten.",
  "Det kan påløpe diagnose- og reparasjonskostnader.",
  "Væskeskadede enheter kan få ytterligere feil etter demontering, og Appledoctor kan ikke garantere reparasjon av slike enheter.",
  "Innleverte enheter oppbevares i maksimalt 30 dager etter at kunden er varslet om ferdigstilt reparasjon.",
];

const initialForm = {
  fullName: "",
  contactPerson: "",
  email: "",
  phone: "",
  address: "",
  postalCode: "",
  city: "",
  devices: [],
  model: "",
  serial: "",
  password: "",
  accessories: [],
  description: "",
  liquidDamage: "",
  previouslyRepaired: "",
  previousRepairDetails: "",
  consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArray = (key, id) => {
    setForm((f) => {
      const arr = f[key].includes(id)
        ? f[key].filter((x) => x !== id)
        : [...f[key], id];
      return { ...f, [key]: arr };
    });
  };

  const canSubmit = useMemo(() => form.consent && !submitting, [form.consent, submitting]);

  const validate = () => {
    const required = {
      fullName: "Fullt navn / firmanavn",
      email: "E-post",
      phone: "Telefon",
      address: "Adresse",
      postalCode: "Postnummer",
      city: "Poststed",
      description: "Beskrivelse av problem",
    };
    for (const [k, label] of Object.entries(required)) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error(`Fyll inn "${label}".`);
        return false;
      }
    }
    if (form.devices.length === 0) {
      toast.error("Velg minst én enhet.");
      return false;
    }
    if (!form.liquidDamage) {
      toast.error("Svar på spørsmålet om væskeskade.");
      return false;
    }
    if (!form.previouslyRepaired) {
      toast.error("Svar på spørsmålet om tidligere reparasjon.");
      return false;
    }
    if (form.previouslyRepaired === "ja" && !form.previousRepairDetails.trim()) {
      toast.error("Fyll inn av hvem og når enheten ble reparert tidligere.");
      return false;
    }
    if (!form.consent) {
      toast.error("Du må godkjenne vilkårene før innsending.");
      return false;
    }
    return true;
  };

  const labelFor = (opts, ids) =>
    opts.filter((o) => ids.includes(o.id)).map((o) => o.label).join(", ");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        navn_firmanavn: form.fullName,
        kontaktperson: form.contactPerson,
        epost: form.email,
        telefon: form.phone,
        adresse: form.address,
        postnummer: form.postalCode,
        poststed: form.city,
        enheter: labelFor(DEVICE_OPTIONS, form.devices),
        modell: form.model,
        serienummer: form.serial,
        passord_pin: form.password,
        tilbehor: labelFor(ACCESSORY_OPTIONS, form.accessories),
        beskrivelse: form.description,
        vaeskeskade: form.liquidDamage,
        tidligere_reparert: form.previouslyRepaired,
        tidligere_reparert_detaljer: form.previousRepairDetails,
        vilkar_godkjent: form.consent ? "Ja" : "Nei",
        _subject: `Ny forespørsel fra ${form.fullName}`,
      };

      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Takk! Vi tar kontakt med deg så snart som mulig.");
        setForm(initialForm);
      } else {
        const data = await res.json().catch(() => ({}));
        const msg =
          data?.errors?.[0]?.message ||
          "Noe gikk galt. Prøv igjen, eller send oss en e-post.";
        toast.error(msg);
      }
    } catch {
      toast.error("Kunne ikke sende skjemaet. Sjekk nettforbindelsen og prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-2 h-11 bg-white border-black/10 rounded-xl focus-visible:ring-2 focus-visible:ring-[#1D1D1F]/30";

  return (
    <section
      id="kontakt"
      className="py-24 md:py-32 bg-[#F7F7F7]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <span className="text-xs uppercase tracking-[0.18em] text-[#86868B]">
                Kontakt
              </span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
                La oss fikse Apple-en din.
              </h2>
              <p className="mt-5 text-lg text-[#1D1D1F]/70 leading-relaxed">
                Fortell oss kort hva som er galt, så får du et uforpliktet tilbud.
              </p>

              <div className="mt-10 space-y-6 text-base">
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <Mail size={20} strokeWidth={1.75} className="mt-0.5 text-[#1D3557]" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#86868B] font-medium">E-post</div>
                    <a
                      href="mailto:post@appledoctor.no"
                      className="text-lg font-semibold text-[#1D1D1F] hover:text-[#1D3557] transition-colors"
                    >
                      post@appledoctor.no
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin size={20} strokeWidth={1.75} className="mt-0.5 text-[#1D3557]" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#86868B] font-medium">Postadresse</div>
                    <span className="block text-lg font-semibold text-[#1D1D1F] leading-snug">
                      Apple Doctor
                      <br />
                      Bassengvegen 31, 2006 Løvenstad
                    </span>
                    <p
                      className="mt-2 text-xs text-[#86868B] leading-relaxed"
                      data-testid="contact-address-disclaimer"
                    >
                      Dette er kun en postadresse. Vi tar ikke imot kunder fysisk.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 rounded-2xl overflow-hidden border border-black/[0.05]">
                <img
                  src="https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?auto=format&fit=crop&w=900&q=70"
                  alt="MacBook på arbeidsbenk"
                  className="w-full h-56 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <form
              action={FORMSPREE_ENDPOINT}
              method="POST"
              onSubmit={handleSubmit}
              className="rounded-3xl bg-white p-6 sm:p-10 border border-black/[0.06] space-y-10"
              data-testid="contact-form"
              noValidate
            >
              {/* Customer info */}
              <fieldset className="space-y-5">
                <legend className="font-display text-lg font-semibold tracking-tight text-[#1D1D1F]">
                  Kundeinformasjon
                </legend>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="fullName" className="text-sm font-medium text-[#1D1D1F]">
                      Fullt navn / firmanavn <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="fullName"
                      name="navn_firmanavn"
                      value={form.fullName}
                      onChange={(e) => setField("fullName", e.target.value)}
                      placeholder="Ditt navn eller firma"
                      className={inputCls}
                      data-testid="input-fullname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson" className="text-sm font-medium text-[#1D1D1F]">
                      Kontaktperson <span className="text-[#86868B] font-normal">(for firma)</span>
                    </Label>
                    <Input
                      id="contactPerson"
                      name="kontaktperson"
                      value={form.contactPerson}
                      onChange={(e) => setField("contactPerson", e.target.value)}
                      placeholder="Hvem skal vi kontakte?"
                      className={inputCls}
                      data-testid="input-contact-person"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-[#1D1D1F]">
                      E-post <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="epost"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="navn@eksempel.no"
                      className={inputCls}
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-[#1D1D1F]">
                      Telefon <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      name="telefon"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="+47 000 00 000"
                      className={inputCls}
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="address" className="text-sm font-medium text-[#1D1D1F]">
                      Adresse <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address"
                      name="adresse"
                      value={form.address}
                      onChange={(e) => setField("address", e.target.value)}
                      placeholder="Gateadresse"
                      className={inputCls}
                      data-testid="input-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postalCode" className="text-sm font-medium text-[#1D1D1F]">
                      Postnummer <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postalCode"
                      name="postnummer"
                      inputMode="numeric"
                      value={form.postalCode}
                      onChange={(e) => setField("postalCode", e.target.value)}
                      placeholder="0000"
                      className={inputCls}
                      data-testid="input-postal-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-sm font-medium text-[#1D1D1F]">
                      Poststed <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      name="poststed"
                      value={form.city}
                      onChange={(e) => setField("city", e.target.value)}
                      placeholder="By"
                      className={inputCls}
                      data-testid="input-city"
                    />
                  </div>
                </div>
              </fieldset>

              {/* Device */}
              <fieldset className="space-y-5">
                <legend className="font-display text-lg font-semibold tracking-tight text-[#1D1D1F]">
                  Enhet og produkt
                </legend>

                <div>
                  <Label className="text-sm font-medium text-[#1D1D1F]">
                    Hvilken enhet gjelder det? <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="device-checkboxes">
                    {DEVICE_OPTIONS.map((opt) => (
                      <label
                        key={opt.id}
                        className="flex items-center gap-3 rounded-xl bg-white border border-black/10 px-3.5 py-3 cursor-pointer hover:border-[#1D1D1F]/30 transition-colors"
                      >
                        <Checkbox
                          name="enheter"
                          value={opt.label}
                          checked={form.devices.includes(opt.id)}
                          onCheckedChange={() => toggleArray("devices", opt.id)}
                          data-testid={`device-${opt.id}`}
                          className="data-[state=checked]:bg-[#1D1D1F] data-[state=checked]:border-[#1D1D1F]"
                        />
                        <span className="text-sm text-[#1D1D1F]">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="model" className="text-sm font-medium text-[#1D1D1F]">
                      Modell
                    </Label>
                    <Input
                      id="model"
                      name="modell"
                      value={form.model}
                      onChange={(e) => setField("model", e.target.value)}
                      placeholder="f.eks. MacBook Pro 14 (2023)"
                      className={inputCls}
                      data-testid="input-model"
                    />
                  </div>
                  <div>
                    <Label htmlFor="serial" className="text-sm font-medium text-[#1D1D1F]">
                      Serienummer
                    </Label>
                    <Input
                      id="serial"
                      name="serienummer"
                      value={form.serial}
                      onChange={(e) => setField("serial", e.target.value)}
                      placeholder="Finnes i Innstillinger / Om denne Mac-en"
                      className={inputCls}
                      data-testid="input-serial"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="password" className="text-sm font-medium text-[#1D1D1F]">
                      Passord / PIN <span className="text-[#86868B] font-normal">(valgfritt)</span>
                    </Label>
                    <Input
                      id="password"
                      name="passord_pin"
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      placeholder="Kun hvis nødvendig for testing"
                      className={inputCls}
                      data-testid="input-password"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#1D1D1F]">
                    Tilbehør som sendes med
                  </Label>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="accessory-checkboxes">
                    {ACCESSORY_OPTIONS.map((opt) => (
                      <label
                        key={opt.id}
                        className="flex items-center gap-3 rounded-xl bg-white border border-black/10 px-3.5 py-3 cursor-pointer hover:border-[#1D1D1F]/30 transition-colors"
                      >
                        <Checkbox
                          name="tilbehor"
                          value={opt.label}
                          checked={form.accessories.includes(opt.id)}
                          onCheckedChange={() => toggleArray("accessories", opt.id)}
                          data-testid={`accessory-${opt.id}`}
                          className="data-[state=checked]:bg-[#1D1D1F] data-[state=checked]:border-[#1D1D1F]"
                        />
                        <span className="text-sm text-[#1D1D1F]">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>

              {/* Problem */}
              <fieldset className="space-y-5">
                <legend className="font-display text-lg font-semibold tracking-tight text-[#1D1D1F]">
                  Feilbeskrivelse
                </legend>

                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-[#1D1D1F]">
                    Beskrivelse av problem <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="beskrivelse"
                    rows={5}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Fortell oss hva som er galt, når det oppstod, og eventuelt hva som er prøvd."
                    className="mt-2 bg-white border-black/10 rounded-xl focus-visible:ring-2 focus-visible:ring-[#1D1D1F]/30 resize-none"
                    data-testid="textarea-description"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#1D1D1F]">
                    Har enheten vært utsatt for væskeskade? <span className="text-red-500">*</span>
                  </Label>
                  <RadioGroup
                    name="vaeskeskade"
                    value={form.liquidDamage}
                    onValueChange={(v) => setField("liquidDamage", v)}
                    className="mt-3 flex gap-3"
                    data-testid="radio-liquid-damage"
                  >
                    {[
                      { v: "ja", label: "Ja" },
                      { v: "nei", label: "Nei" },
                      { v: "vet-ikke", label: "Vet ikke" },
                    ].map((o) => (
                      <label
                        key={o.v}
                        className="flex items-center gap-2 rounded-xl bg-white border border-black/10 px-4 py-3 cursor-pointer hover:border-[#1D1D1F]/30 transition-colors"
                      >
                        <RadioGroupItem
                          value={o.v}
                          data-testid={`radio-liquid-${o.v}`}
                          className="text-[#1D1D1F] border-black/30"
                        />
                        <span className="text-sm text-[#1D1D1F]">{o.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#1D1D1F]">
                    Har enheten blitt reparert eller åpnet tidligere? <span className="text-red-500">*</span>
                  </Label>
                  <RadioGroup
                    name="tidligere_reparert"
                    value={form.previouslyRepaired}
                    onValueChange={(v) => setField("previouslyRepaired", v)}
                    className="mt-3 flex gap-3"
                    data-testid="radio-previously-repaired"
                  >
                    {[
                      { v: "ja", label: "Ja" },
                      { v: "nei", label: "Nei" },
                    ].map((o) => (
                      <label
                        key={o.v}
                        className="flex items-center gap-2 rounded-xl bg-white border border-black/10 px-4 py-3 cursor-pointer hover:border-[#1D1D1F]/30 transition-colors"
                      >
                        <RadioGroupItem
                          value={o.v}
                          data-testid={`radio-previous-${o.v}`}
                          className="text-[#1D1D1F] border-black/30"
                        />
                        <span className="text-sm text-[#1D1D1F]">{o.label}</span>
                      </label>
                    ))}
                  </RadioGroup>

                  {form.previouslyRepaired === "ja" && (
                    <div className="mt-4">
                      <Label htmlFor="previousRepairDetails" className="text-sm font-medium text-[#1D1D1F]">
                        Hvis ja — av hvem og når? <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="previousRepairDetails"
                        name="tidligere_reparert_detaljer"
                        value={form.previousRepairDetails}
                        onChange={(e) => setField("previousRepairDetails", e.target.value)}
                        placeholder="f.eks. Apple Store, mars 2024"
                        className={inputCls}
                        data-testid="input-previous-repair-details"
                      />
                    </div>
                  )}
                </div>
              </fieldset>

              {/* Consent */}
              <div className="rounded-2xl bg-[#F7F7F7] border border-black/10 p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    name="vilkar_godkjent"
                    value="Ja"
                    checked={form.consent}
                    onCheckedChange={(v) => setField("consent", Boolean(v))}
                    data-testid="checkbox-consent"
                    className="mt-0.5 data-[state=checked]:bg-[#1D1D1F] data-[state=checked]:border-[#1D1D1F]"
                  />
                  <span className="text-sm text-[#1D1D1F] leading-relaxed">
                    Jeg godkjenner Appledoctors vilkår for innlevering og reparasjon.
                    <span className="text-red-500"> *</span>
                  </span>
                </label>

                <ul className="mt-4 ml-7 space-y-1.5 text-xs text-[#86868B] leading-relaxed list-disc list-outside" data-testid="consent-terms">
                  {TERMS.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-[#86868B]">
                  Felt merket med <span className="text-red-500">*</span> er obligatoriske.
                </p>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="rounded-full bg-[#1D1D1F] hover:bg-[#0a0a0a] text-white px-7 h-12 text-base font-medium min-w-[200px] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Sender…
                    </>
                  ) : (
                    "Send forespørsel"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
